import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Plus, FileText, GripVertical, Eye, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicationAssemblyPage() {
  const [searchParams] = useSearchParams();
  const publicationId = searchParams.get('publicationId');
  
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publicationTitle, setPublicationTitle] = useState('');
  const [publicationAuthor, setPublicationAuthor] = useState('');
  const [publicationAffiliation, setPublicationAffiliation] = useState('');
  const [citationStyle, setCitationStyle] = useState('APA');
  const [tone, setTone] = useState('academic');
  const [existingPublicationId, setExistingPublicationId] = useState(null);
  
  // Session management
  const [savedSessions, setSavedSessions] = useState([]);
  const [showSessionsModal, setShowSessionsModal] = useState(false);

  // Load saved sessions from localStorage on mount
  useEffect(() => {
    const sessions = localStorage.getItem('publication-assembly-sessions');
    if (sessions) {
      try {
        setSavedSessions(JSON.parse(sessions));
      } catch (error) {
        console.error('Error loading sessions:', error);
      }
    }
  }, []);

  useEffect(() => {
    // If publicationId is provided, load that publication
    if (publicationId) {
      loadPublication(publicationId);
    } else {
      // Try to load from session storage first
      const savedChapters = sessionStorage.getItem('publicationChapters');
      if (savedChapters) {
        setChapters(JSON.parse(savedChapters));
        setLoading(false);
      } else {
        fetchChapters();
      }
    }
  }, [publicationId]);

  const loadPublication = async (pubId) => {
    try {
      const response = await axios.get(`${API}/admin/generation/publications/${pubId}`, {
        withCredentials: true
      });
      const pub = response.data.publication;
      
      setPublicationTitle(pub.title || '');
      setPublicationAuthor(pub.author || '');
      setPublicationAffiliation(pub.affiliation || '');
      setCitationStyle(pub.citation_style || 'APA');
      setTone(pub.tone || 'academic');
      setChapters(pub.chapters || []);
      setExistingPublicationId(pubId);
      setLoading(false);
      toast.success('Publication loaded for editing');
    } catch (error) {
      console.error('Error loading publication:', error);
      toast.error('Failed to load publication');
      setLoading(false);
    }
  };

  const fetchChapters = async () => {
    try {
      const response = await axios.get(`${API}/admin/generation/chapters`, {
        withCredentials: true
      });
      setChapters(response.data.chapters);
    } catch (error) {
      console.error('Error fetching chapters:', error);
      toast.error('Failed to load chapters');
    } finally {
      setLoading(false);
    }
  };

  const handleReorderChapters = (fromIndex, toIndex) => {
    const newChapters = [...chapters];
    const [removed] = newChapters.splice(fromIndex, 1);
    newChapters.splice(toIndex, 0, removed);
    
    // Update order numbers
    newChapters.forEach((c, idx) => {
      c.order = idx + 1;
    });
    
    setChapters(newChapters);
    toast.success('Chapters reordered');
  };

  const handleRemoveChapter = (chapterId) => {
    if (window.confirm('Remove this chapter from publication?')) {
      setChapters(prev => prev.filter(c => c.id !== chapterId));
      toast.success('Chapter removed');
    }
  };
  
  const handleSaveSession = () => {
    if (!publicationTitle) {
      toast.error('Please enter a publication title before saving');
      return;
    }
    
    const sessionData = {
      id: `session-${Date.now()}`,
      title: publicationTitle,
      author: publicationAuthor,
      affiliation: publicationAffiliation,
      citationStyle,
      tone,
      chapters,
      savedAt: new Date().toISOString()
    };
    
    const currentSessions = [...savedSessions];
    
    // Keep only the 5 most recent sessions
    if (currentSessions.length >= 5) {
      currentSessions.shift(); // Remove oldest
    }
    
    currentSessions.push(sessionData);
    setSavedSessions(currentSessions);
    localStorage.setItem('publication-assembly-sessions', JSON.stringify(currentSessions));
    toast.success(`Session "${publicationTitle}" saved!`);
  };
  
  const handleLoadSession = (session) => {
    setPublicationTitle(session.title);
    setPublicationAuthor(session.author);
    setPublicationAffiliation(session.affiliation);
    setCitationStyle(session.citationStyle);
    setTone(session.tone);
    setChapters(session.chapters);
    setShowSessionsModal(false);
    toast.success(`Loaded session: ${session.title}`);
  };
  
  const handleDeleteSession = (sessionId) => {
    const updated = savedSessions.filter(s => s.id !== sessionId);
    setSavedSessions(updated);
    localStorage.setItem('publication-assembly-sessions', JSON.stringify(updated));
    toast.success('Session deleted');
  };

  const handleCreatePublication = async () => {
    if (!publicationTitle || !publicationAuthor) {
      toast.error('Please enter title and author');
      return;
    }

    if (chapters.length === 0) {
      toast.error('No chapters available. Please create chapters first.');
      return;
    }

    try {
      const chapterIds = chapters.map(c => c.id);
      
      if (existingPublicationId) {
        // Update existing publication
        await axios.put(
          `${API}/admin/generation/publications/${existingPublicationId}`,
          {
            title: publicationTitle,
            author: publicationAuthor,
            affiliation: publicationAffiliation || null,
            citation_style: citationStyle,
            tone: tone,
            chapter_ids: chapterIds
          },
          { withCredentials: true }
        );
        toast.success('Publication updated successfully!');
      } else {
        // Create new publication
        await axios.post(
          `${API}/admin/generation/publications`,
          {
            title: publicationTitle,
            author: publicationAuthor,
            affiliation: publicationAffiliation || null,
            citation_style: citationStyle,
            tone: tone,
            chapter_ids: chapterIds
          },
          { withCredentials: true }
        );
        toast.success('Publication created successfully!');
      }

      // Clear session storage
      sessionStorage.removeItem('publicationChapters');
      
      // Navigate to vault
      setTimeout(() => {
        window.location.href = '/admin/publication-vault';
      }, 1000);
    } catch (error) {
      console.error('Error with publication:', error);
      toast.error(error.response?.data?.detail || `Failed to ${existingPublicationId ? 'update' : 'create'} publication`);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-[#F4E4C1] to-[#E8D5B7] py-16">
        <div className="max-w-5xl mx-auto px-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => window.location.href = '/admin/generation'}
              className="retro-button flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Generation
            </button>
            
            <h1 className="text-4xl retro-title text-[#6B4423]">Publication Assembly</h1>
            
            <div className="flex gap-3">
              {/* Save Session Button */}
              <button
                onClick={handleSaveSession}
                className="retro-button-small bg-[#4D9E7C] hover:bg-[#3D8E6C]"
                title="Save current session"
              >
                Save Session
              </button>
              
              {/* Load Sessions Button */}
              <button
                onClick={() => setShowSessionsModal(true)}
                className="retro-button-small bg-[#E8934D] hover:bg-[#D8834D]"
                title="Load saved sessions"
              >
                Sessions ({savedSessions.length}/5)
              </button>
              
              <button
                onClick={handleCreatePublication}
                disabled={chapters.length === 0 || !publicationTitle || !publicationAuthor}
                className="retro-button flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FileText className="w-4 h-4" />
                {existingPublicationId ? 'Update Publication' : 'Create Publication'}
              </button>
            </div>
          </div>

          {/* Publication Details */}
          <div className="admin-card mb-6">
            <h2 className="text-2xl font-bold text-white mb-4">Publication Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-white font-bold text-lg mb-2">Title *</label>
                <input
                  type="text"
                  value={publicationTitle}
                  onChange={(e) => setPublicationTitle(e.target.value)}
                  placeholder="Enter publication title"
                  className="w-full px-4 py-2 border-2 border-[#8B6F47] rounded focus:outline-none focus:border-[#6B4423] text-gray-900"
                />
              </div>
              
              <div>
                <label className="block text-white font-bold text-lg mb-2">Author *</label>
                <input
                  type="text"
                  value={publicationAuthor}
                  onChange={(e) => setPublicationAuthor(e.target.value)}
                  placeholder="Author name"
                  className="w-full px-4 py-2 border-2 border-[#8B6F47] rounded focus:outline-none focus:border-[#6B4423] text-gray-900"
                />
              </div>
              
              <div>
                <label className="block text-white font-bold text-lg mb-2">Affiliation</label>
                <input
                  type="text"
                  value={publicationAffiliation}
                  onChange={(e) => setPublicationAffiliation(e.target.value)}
                  placeholder="University or organization"
                  className="w-full px-4 py-2 border-2 border-[#8B6F47] rounded focus:outline-none focus:border-[#6B4423] text-gray-900"
                />
              </div>
              
              <div>
                <label className="block text-white font-bold text-lg mb-2">Citation Style</label>
                <select
                  value={citationStyle}
                  onChange={(e) => setCitationStyle(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-[#8B6F47] rounded focus:outline-none focus:border-[#6B4423] text-gray-900"
                >
                  <option value="APA">APA</option>
                  <option value="MLA">MLA</option>
                  <option value="Chicago">Chicago</option>
                  <option value="IEEE">IEEE</option>
                </select>
              </div>
              
              <div>
                <label className="block text-white font-bold text-lg mb-2">Tone</label>
                <select
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-[#8B6F47] rounded focus:outline-none focus:border-[#6B4423] text-gray-900"
                >
                  <option value="academic">Academic</option>
                  <option value="narrative">Narrative</option>
                  <option value="technical">Technical</option>
                </select>
              </div>
            </div>
          </div>

          {/* Chapters List */}
          <div className="admin-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-[#6B4423]">
                Chapters ({chapters.length})
              </h2>
              
              {chapters.length === 0 && (
                <button
                  onClick={() => window.location.href = '/admin/generation'}
                  className="retro-button flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Create Chapter
                </button>
              )}
            </div>

            {loading ? (
              <div className="text-center py-8">Loading chapters...</div>
            ) : chapters.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <p className="text-xl text-gray-600 mb-4">No chapters yet</p>
                <p className="text-gray-500 mb-6">
                  Create chapters in the Generation page first
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {chapters.map((chapter, index) => (
                  <div key={chapter.id} className="relative group">
                    {/* Drag Handle */}
                    <div className="absolute left-0 top-0 bottom-0 w-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-move">
                      <GripVertical className="w-5 h-5 text-gray-400" />
                    </div>
                    
                    <div className="p-4 bg-white border-2 border-[#8B6F47] rounded hover:border-[#6B4423] transition-colors ml-8">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-semibold text-gray-600">
                              Chapter {index + 1}
                            </span>
                          </div>
                          <h3 className="text-lg font-semibold text-[#6B4423]">{chapter.title}</h3>
                          <p className="text-sm text-gray-700 mt-1">
                            {chapter.paragraph_ids?.length || 0} paragraph(s)
                          </p>
                        </div>
                        
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleRemoveChapter(chapter.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Remove"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Placeholder for Phase 6 features */}
            <div className="mt-6 p-4 bg-[#F4E4C1] rounded border-2 border-[#8B6F47]">
              <p className="text-[#6B4423]">
                <strong>Coming in Phase 6:</strong> Cover design, headers/footers, page numbers, margins, templates, figure/table numbering
              </p>
            </div>
          </div>
        </div>
        
        {/* Sessions Modal */}
        {showSessionsModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-[#F4E4C1] rounded-lg shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-y-auto">
              <div className="sticky top-0 bg-[#6B4423] text-white p-6 flex justify-between items-center">
                <h2 className="text-2xl font-bold">Saved Sessions ({savedSessions.length}/5)</h2>
                <button
                  onClick={() => setShowSessionsModal(false)}
                  className="text-white hover:text-[#E85D4D] text-2xl"
                >
                  ×
                </button>
              </div>
              
              <div className="p-6">
                {savedSessions.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-[#6B4423] text-lg mb-4">No saved sessions yet</p>
                    <p className="text-[#8B6F47]">Click "Save Session" to save your current work</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {savedSessions.map((session) => (
                      <div
                        key={session.id}
                        className="bg-white border-2 border-[#8B6F47] rounded-lg p-4 hover:border-[#6B4423] transition-colors"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-[#2C1810] mb-1">{session.title}</h3>
                            <div className="text-sm text-[#6B4423] space-y-1">
                              <p>Author: {session.author}</p>
                              <p>Chapters: {session.chapters.length} | Style: {session.citationStyle}</p>
                              <p>Saved: {new Date(session.savedAt).toLocaleString()}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleLoadSession(session)}
                              className="retro-button-small bg-[#4D9E7C] hover:bg-[#3D8E6C]"
                            >
                              Load
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm(`Delete session "${session.title}"?`)) {
                                  handleDeleteSession(session.id);
                                }
                              }}
                              className="retro-button-small bg-[#E85D4D] hover:bg-[#D84D3D]"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
